import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { useAuth } from "@/lib/auth";
import { Header } from "@/components/site/Header";
import { Footer } from "@/components/site/Footer";
import { Seo } from "@/components/seo/Seo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_BADGE = {
  pending: { bg: "bg-amber-50 text-amber-800 border-amber-200", label: "Verification pending" },
  approved: { bg: "bg-emerald-50 text-emerald-700 border-emerald-200", label: "Verified clinic" },
  rejected: { bg: "bg-red-50 text-red-700 border-red-200", label: "Application rejected" },
};

export const Account = () => {
  const { user, ready, logout } = useAuth();
  const nav = useNavigate();
  const [orders, setOrders] = useState([]);
  const [params] = useSearchParams();

  useEffect(() => {
    if (ready && !user) nav("/login");
  }, [ready, user, nav]);

  useEffect(() => {
    if (!user) return;
    axios.get(`${API}/orders`).then((r) => setOrders(r.data || [])).catch(() => {});
  }, [user]);

  if (!user) return null;
  const badge = STATUS_BADGE[user.verification_status] || STATUS_BADGE.pending;
  const welcome = params.get("welcome") === "1";

  return (
    <>
      <Seo title="My account" path="/account" noindex />
      <Header />
      <main className="max-w-[1100px] mx-auto px-6 pt-28 md:pt-32 pb-24" data-testid="account-page">
        {welcome && user.verification_status === "pending" && (
          <div className="mb-8 border border-amber-200 bg-amber-50 px-5 py-4 text-sm text-amber-900" data-testid="welcome-banner">
            <strong className="font-bold">Welcome.</strong> Your application is under review. You can browse the catalog, but checkout will unlock once your clinic is verified (usually within 24h).
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-12 gap-12">
          <aside className="md:col-span-4 space-y-4">
            <div className="text-[11px] tracking-[0.3em] uppercase text-neutral-500 font-medium">Account</div>
            <h1 className="font-heading text-3xl md:text-4xl font-black tracking-tight leading-tight" data-testid="account-name">
              {user.name}
            </h1>
            <div className={`inline-flex items-center gap-2 border px-3 py-1.5 text-[11px] tracking-wide font-bold ${badge.bg}`} data-testid="verification-status">
              {badge.label}
            </div>
            <dl className="space-y-2 pt-4 border-t border-neutral-200 text-sm">
              <Row label="Email" value={user.email} />
              <Row label="Clinic" value={user.clinic_name} />
              <Row label="License" value={user.license_number} />
              <Row label="Country" value={user.country} />
              <Row label="Phone" value={user.phone} />
            </dl>
            <button
              onClick={async () => { await logout(); nav("/"); }}
              className="mt-6 w-full h-11 border border-black text-sm font-medium hover:bg-black hover:text-white transition-colors"
              data-testid="logout-btn"
            >
              Sign out
            </button>
          </aside>

          <section className="md:col-span-8" data-testid="orders-section">
            <div className="text-[11px] tracking-[0.3em] uppercase text-neutral-500 mb-3 font-medium">Orders</div>
            <h2 className="font-heading text-3xl font-black tracking-tight mb-6">Your purchase history.</h2>
            {orders.length === 0 ? (
              <div className="border border-dashed border-neutral-300 px-6 py-10 text-center text-sm text-neutral-500" data-testid="no-orders">
                No orders yet. <Link to="/" className="border-b border-black pb-0.5 font-medium">Browse catalog →</Link>
              </div>
            ) : (
              <div className="space-y-3">
                {orders.map((o) => (
                  <div key={o.id} className="border border-neutral-200 p-5" data-testid={`order-${o.id}`}>
                    <div className="flex items-center justify-between gap-4 flex-wrap">
                      <div>
                        <div className="font-heading font-black tracking-tight">{o.order_number}</div>
                        <div className="text-xs text-neutral-500">{new Date(o.created_at).toLocaleString()}</div>
                      </div>
                      <div className="text-right">
                        <div className="font-bold">${o.total.toFixed(2)}</div>
                        <div className="text-[10px] tracking-[0.2em] uppercase text-neutral-500">{o.status}</div>
                      </div>
                    </div>
                    <div className="mt-3 pt-3 border-t border-neutral-100 text-xs text-neutral-600">
                      {o.items.length} item{o.items.length !== 1 ? "s" : ""} · {o.shipping?.country}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </section>
        </div>
      </main>
      <Footer />
    </>
  );
};

const Row = ({ label, value }) => (
  <div className="flex justify-between gap-4">
    <dt className="text-[10px] tracking-[0.2em] uppercase text-neutral-500 font-medium">{label}</dt>
    <dd className="font-medium text-black truncate">{value || "—"}</dd>
  </div>
);
